import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Input,
  Button,
  Select,
  Icon,
  Spinner,
  Alert,
  SimpleGrid,
  Link,
} from '@chakra-ui/react'
import { FiSearch, FiRefreshCw, FiDollarSign } from 'react-icons/fi'
import { apiConfig } from '../../config/api'
import { DataTable, Pagination } from '../../components/admin/DataTable'
import { StatusBadge } from '../../components/admin/StatusBadge'
import { StatsCard } from '../../components/admin/StatsCard'

interface Transaction {
  id: string
  campaign_id: string
  practitioner_id: string
  practitioner_name?: string
  amount_cents: number
  currency: string
  status: string
  stripe_payment_intent_id?: string
  created_at?: string
}

interface TransactionsSummary {
  total_revenue_cents: number
  revenue_this_month_cents: number
  transaction_count: number
  avg_transaction_value_cents: number
}

export const TransactionsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [summary, setSummary] = useState<TransactionsSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingSummary, setIsLoadingSummary] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || ''
  )
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const fetchTransactions = async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      params.append('limit', pageSize.toString())
      params.append('offset', ((currentPage - 1) * pageSize).toString())

      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/transactions?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load transactions')
      }

      const result = await response.json()
      setTransactions(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSummary = async () => {
    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setIsLoadingSummary(false)
      return
    }

    try {
      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/transactions/summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        setSummary(result)
      }
    } catch (err) {
      console.error('Failed to load summary:', err)
    } finally {
      setIsLoadingSummary(false)
    }
  }

  useEffect(() => {
    fetchTransactions()
    fetchSummary()
  }, [statusFilter, currentPage])

  useEffect(() => {
    const params = new URLSearchParams()
    if (statusFilter) params.set('status', statusFilter)
    setSearchParams(params, { replace: true })
  }, [statusFilter, setSearchParams])

  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      transaction.id.toLowerCase().includes(searchLower) ||
      transaction.campaign_id.toLowerCase().includes(searchLower) ||
      transaction.practitioner_name?.toLowerCase().includes(searchLower) ||
      transaction.stripe_payment_intent_id?.toLowerCase().includes(searchLower)
    )
  })

  const columns = [
    {
      key: 'id',
      label: 'Transaction ID',
      render: (transaction: Transaction) => (
        <Text fontFamily="mono" fontSize="xs">
          {transaction.id.substring(0, 12)}...
        </Text>
      ),
    },
    {
      key: 'practitioner_name',
      label: 'Practitioner',
      render: (transaction: Transaction) => (
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">
            {transaction.practitioner_name || 'Unknown'}
          </Text>
          <Link
            color="blue.500"
            fontSize="xs"
            onClick={() =>
              navigate(`/admin/practitioners/${transaction.practitioner_id}`)
            }
            cursor="pointer"
          >
            View Profile
          </Link>
        </VStack>
      ),
    },
    {
      key: 'campaign_id',
      label: 'Campaign',
      render: (transaction: Transaction) => (
        <Link
          color="blue.500"
          fontSize="sm"
          onClick={() =>
            navigate(`/admin/campaigns/${transaction.campaign_id}`)
          }
          cursor="pointer"
        >
          {transaction.campaign_id.substring(0, 12)}...
        </Link>
      ),
    },
    {
      key: 'amount_cents',
      label: 'Amount',
      render: (transaction: Transaction) => (
        <Text fontWeight="bold">
          {formatCurrency(transaction.amount_cents)}
        </Text>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (transaction: Transaction) => (
        <StatusBadge status={transaction.status} />
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (transaction: Transaction) => {
        if (!transaction.created_at) return <Text color="gray.400">-</Text>
        const date = new Date(transaction.created_at)
        return <Text fontSize="sm">{date.toLocaleDateString()}</Text>
      },
    },
  ]

  return (
    <Container maxW="7xl">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Transactions
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Payment history and revenue tracking
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={() => {
              fetchTransactions()
              fetchSummary()
            }}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Summary Stats */}
        {summary && (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
            <StatsCard
              label="Total Revenue"
              value={formatCurrency(summary.total_revenue_cents)}
              icon={FiDollarSign}
              iconColor="green.500"
            />
            <StatsCard
              label="This Month"
              value={formatCurrency(summary.revenue_this_month_cents)}
              icon={FiDollarSign}
              iconColor="blue.500"
            />
            <StatsCard
              label="Total Transactions"
              value={summary.transaction_count.toLocaleString()}
              icon={FiDollarSign}
              iconColor="purple.500"
            />
            <StatsCard
              label="Avg Transaction"
              value={formatCurrency(summary.avg_transaction_value_cents)}
              icon={FiDollarSign}
              iconColor="orange.500"
            />
          </SimpleGrid>
        )}

        {/* Filters */}
        <Card.Root>
          <Card.Body p={4}>
            <HStack gap={4} flexWrap="wrap">
              <HStack flex={1} minW="200px">
                <Icon as={FiSearch} color="gray.400" />
                <Input
                  placeholder="Search by ID, campaign, or practitioner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </HStack>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="All Statuses"
                w="200px"
              >
                <option value="pending">Pending</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
              </Select>
            </HStack>
          </Card.Body>
        </Card.Root>

        {/* Error Alert */}
        {error && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              {error}
            </Alert.Content>
          </Alert.Root>
        )}

        {/* Transactions Table */}
        <Card.Root>
          <Card.Body p={0}>
            <DataTable
              data={filteredTransactions}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No transactions found"
            />
            {!isLoading && filteredTransactions.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredTransactions.length / pageSize)}
                onPageChange={setCurrentPage}
              />
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  )
}

